<template>
  <v-layout class="rounded rounded-md">
    <Navbar
      :displayname="displayname"
      :app="{
        userProfile,
        restApi,
        reset,
      }"
      :is-logged-in="isLoggedIn"
      :user-profile="userProfile"
      :selected-customer="selectedCustomer"
      :auth="auth"
    />
    <v-main style="min-height: 300px; padding-top: 64px;">
      <ProgressSpinner
        :is-loading="isLoading"
        :force-hide-loader="forceHideLoader"
        background-color="#000"
      />
      <IntegratorBar
        v-if="isLoggedIn"
        :is-logged-in="isLoggedIn"
        :user-profile="userProfile"
        :selected-customer="selectedCustomer"
        :set-selected-customer="setSelectedCustomer"
      />
      <RouterView
        style="width: 100%"
        :is-logged-in="isLoggedIn"
        :displayname="displayname"
        :selected-customer="selectedCustomer"
        :app="{
          restApi,
          userProfile,
          isLoading,
          setUserProfile,
          setForceHideLoader,
          setSelectedCustomerById,
          reset,
          showNotification,
        }"
        :auth="auth"
      />
      <v-snackbar
        v-model="showingNotification"
        :timeout="notificationTimeout"
        class="snackbar-notification"
        variants="text"
      >
        {{ nofificationText }}
        <v-btn
          class="snackbar-notification-button"
          variants="text"
          right
          icon
          size="small"
          @click="closeNotification"
        >
          <v-icon
            color="white"
            size="small"
            icon="fas fa-close"
          />
        </v-btn>
      </v-snackbar>
    </v-main>
  </v-layout>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axios from "axios";
import env from "../server.config";
import { AuthProvider } from "./util";
import ProgressSpinner from "./components/loaders/ProgressSpinner.vue";
import IntegratorBar from "./components/integrator-bar/IntegratorBar.vue";
import Navbar from "./components/navbar/Navbar.vue";

export default defineComponent({
  name: "App",
  components: {
    Navbar,
    ProgressSpinner,
    IntegratorBar,
  },
  data() {
    return {
      restApi: {},
      userProfile: {},
      selectedCustomer: {},
      isLoggedIn: false,
      isLoading: 0,
      forceHideLoader: false,
      errorMessage: "",
      showingNotification: false,
      notificationTimeout: 6000,
      nofificationText: "",
      auth: undefined,
    };
  },
  computed: {
    displayname() {
      return this.isLoggedIn
        ? `${this.userProfile.firstName} ${this.userProfile.lastName}`
        : "";
    },
  },
  watch: {
    userProfile(val) {
      // console.log("app::watch userProfile", val);
      if (this.userProfile && Object.keys(this.userProfile).length > 5) {
        this.isLoggedIn = true;
      }
    },
  },
  mounted() {
    // initialize firebase session and user
    this.userProfile = JSON.parse(localStorage.getItem("userProfile"));
    this.auth = new AuthProvider(this.checkFbQxSync);

    let savedSelectedCustomer = JSON.parse(
      localStorage.getItem("selectedCustomer"),
    );
    if (savedSelectedCustomer) {
      this.setSelectedCustomer(savedSelectedCustomer);
    }

    // initialize api using session cookies
    this.restApi = axios.create({
      baseURL: env.apiBaseUrl,
      withCredentials: true, // use session cookies
    });

    // before a request is made start the nprogress
    this.restApi.interceptors.request.use((config) => {
      this.isLoading++;
      return config;
    });

    const reauth = async (api) => {
      const user = await this.auth.qxApiReLogin(api);
      this.setUserProfile(user);
    };
    let _authorizing = null;  // if this were typescript _authorizing: Promise<void> | null

    // before a response is returned stop nprogress
    this.restApi.interceptors.response.use(
      (response) => {
        if (this.isLoading > 0) {
          this.isLoading--;
        }
        return response;
      },
      async (error) => {
        this.isLoading = 0;
        // 401 is JWT validation error
        // 410 is PHP session invalid
        if (401 === error.response.status) {
          if (this.userProfile && Object.keys(this.userProfile).length > 0) {
            // Only do this once - we may have made multiple API calls that will fail
            alert(
              `Your session has expired. You will be redirected to the login page. (${error.response.status})`,
            );
            sessionStorage.setItem(
              "redirectPath",
              JSON.stringify({
                name: this.$router.currentRoute.value.name,
                params: this.$router.currentRoute.value.params,
              }),
            );

            this.$router.push({
              name: "login.alert",
              params: { alert: "login_required" },
            });
            this.reset();
          } else {
            return error;
          }
        } else if (410 === error.response.status) {
          // PHP session lifetime is less than JWT token.  Try to re-login to qxAPI with existing JWT.
          _authorizing ??= reauth(this.restApi)
            .finally(() => _authorizing = null)
            .catch(error => Promise.reject(error));

          let originalRequestConfig = error.config;
          return _authorizing.then(() => {
            // DER - 052025 Since this was a 410, my auth token was still valid so I can still use it.  The reauth() renewed
            // my PHP backend session.  this.app is not valid in this promise, so don't use it.
            //originalRequestConfig.headers['Authorization'] = 'Bearer ' + this.app.userProfile.token;  // new token after re-auth
            return this.restApi.request(originalRequestConfig);
          })

        } else {
          // this propagates error to catch() at caller
          return Promise.reject(error);
        }
      },
    );

    if (this.isLoggedIn) {
      // if user only has one visible customer, select it by default
      const { visibleCompanies } = this.userProfile;
      if (visibleCompanies && visibleCompanies.length === 1) {
        this.selectedCustomer = visibleCompanies[0];
      }
    }
  },
  methods: {
    // clear auth and app state
    reset(clearStorage = true) {
      this.isLoading = 0;
      this.selectedCustomer = {};
      this.userProfile = {};
      this.isLoggedIn = false;
      if (clearStorage) {
        localStorage.clear();
      }
      if (this.$router.currentRoute.value.name !== "login") {
        this.$router.push({ name: "login" });
      }
    },

    checkFbQxSync() {
      /* This is my callback when Firebase auth changes state.  Here are the scenarios:
      1) firebase = undefined, qx = undefined  => no action
      2) firebase = undefined, qx = userX => sign out to kill qx PHP session
      3) firebase = userX, qx = undefined => sign in to qx and activate UI
      4) firebase = userX, qx = userX => no action
      5) firebase = userX, qx = userY => undesirable so sign out of both to clear cache
      */
      const firebaseUser = this.auth.GetCurrentUser();
      if (
        this.userProfile?.email &&
        firebaseUser?.email != this.userProfile?.email
      ) {
        // scenario 2 or 5
        // mismatch between QxAPI stored profile and firebase profile.  Need to reset and sign out to kill PHP session
        this.auth.signOut(this.userProfile, this.restApi, this.reset);
      } else if (firebaseUser?.email && !this.userProfile?.email) {
        // scenario 3
        try {
          const userProfile = JSON.parse(localStorage.getItem("userProfile")); // get the latest, may have been changed by another tab
          if (userProfile?.email) {
            this.setUserProfile(userProfile);
            this.$router.push({ name: "home" });
          }
        } catch (err) {
          console.error(
            "App::checkFbQxSync() json parsing error: ",
            err?.message,
          );
        }
      }
    },

    setUserProfile(userProfile) {
      localStorage.setItem("userProfile", JSON.stringify(userProfile));
      this.userProfile = userProfile;
      const customer = {
        name: userProfile.companyName,
        id: userProfile.companyId,
        customerId: userProfile.customerId,
      };
      const savedSelectedCustomer = JSON.parse(localStorage.getItem("selectedCustomer"));
      const idx = userProfile.visibleCompanies.findIndex((element) => {
        return element.id == savedSelectedCustomer?.id;
      });
      if (idx == -1) {
        this.setSelectedCustomer(customer);
      } else {
        this.setSelectedCustomer(userProfile.visibleCompanies[idx]);
      }
    },

    setSelectedCustomer(val) {
      localStorage.setItem("selectedCustomer", JSON.stringify(val));
      this.selectedCustomer = val;
    },

    setSelectedCustomerById(customerId) {
      const { visibleCompanies } = this.userProfile;
      const selectedCustomer = visibleCompanies.filter(
        (el) => el.id === customerId,
      );
      if (
        selectedCustomer.length > 0 &&
        selectedCustomer[0].id != this.setSelectedCustomer.id
      ) {
        this.setSelectedCustomer(selectedCustomer[0]);
      }
    },

    setForceHideLoader(val) {
      this.forceHideLoader = val;
    },
    showNotification(text, timeout) {
      this.showingNotification = true;
      this.nofificationText = text;
      this.notificationTimeout = timeout;
    },
    closeNotification() {
      this.showingNotification = false;
      this.nofificationText = "";
      this.notificationTimeout = 6000;
    },
  },
});
</script>

<style>
/* import global classes */
@import "./assets/css/styles.css";



.body-content {
  max-width: 100%;
  padding: 0px;
}

.snackbar-notification {
  width: 100%;
}

.snackbar-notification,
.snackbar-notification-button {
  z-index: 700 !important;
}

.snackbar-notification-button {
  margin-left: auto;
}

.v-card--variant-outlined {
  border-color: lightgray;
  border-radius: 0;
}
</style>
